import { action, computed, observable, runInAction } from 'mobx';
import { orderBy } from 'lodash';
import { api } from 'core/util';
import {
  CAPABILITY_KEYS,
  CAPABILITY_TITLES,
  GROUP_HEALTH_LABELS,
  GROUP_ID_PREFIX,
  Statuses
} from '@kentik/ui-shared/agents/constants';
import {
  getRawCapabilityConfigs,
  transformConfigMetadataToOldFormat,
  capabilitySupportsConfig
} from '@kentik/ui-shared/agents/utils';
import { filterConfigMetadataByVersion } from 'app/views/settings/kentikAgent/utils';
import KentikAgentCollection from './KentikAgentCollection';
import KentikAgentClusterCollection from './KentikAgentClusterCollection';

// Short disambiguation suffixes for cluster option labels - CAPABILITY_TITLES entries (e.g. 'Flow Proxy (Edge Processing)') are too long/already parenthesized to nest in "Cluster (...)".
const CAPABILITY_LABEL_SUFFIX = {
  [CAPABILITY_KEYS.RANGER]: 'Ranger',
  [CAPABILITY_KEYS.KPROXY]: 'Flow Proxy'
};

class KentikAgentStore {
  @observable
  loading = false;

  agentLookups = {};

  // Cache for raw capability metadata from the API - keyed by capabilityKey
  @observable.shallow
  capabilityMetadataCache = {};

  // Cache for transformed + version-filtered config metadata - keyed by "capabilityKey@version"
  transformedConfigCache = {};

  // Promise to track if metadata is being fetched
  metadataFetchPromise = null;

  // Whether a metadata fetch has completed at least once
  @observable
  metadataHasFetched = false;

  collection = null;

  unregisteredCollection = null;

  clusters = null;

  initialize() {
    // get all agents (incl unregistered)
    this.collection = new KentikAgentCollection({ registered: true, unregistered: true, store: this.store });

    // For the Deployment path.  Not loaded initially
    this.unregisteredCollection = new KentikAgentCollection({
      registered: false,
      unregistered: true,
      store: this.store
    });

    this.clusters = new KentikAgentClusterCollection();
  }

  @action
  setLoading(isLoading = true) {
    this.loading = isLoading;
  }

  fetchDownAgents() {
    return api.get('/api/ui/kentik-agent/agents/down-count');
  }

  fetchAlertPolicyCoverage() {
    return api.get('/api/ui/kentik-agent/agents/alert-policy-coverage');
  }

  @computed
  get findLongestHearbeat() {
    let longestHearbeat = 30;
    if (this.collection?.size > 0) {
      this.collection.each((item) => {
        if (parseInt(item?.heartBeatPeriod) > longestHearbeat) {
          longestHearbeat = parseInt(item.heartBeatPeriod);
        }
      });
    }
    return longestHearbeat;
  }

  kentikAgentOptionsWithCapability(capabilityKey) {
    const options = this.collection
      .map((agent) => {
        const cap = agent.getCapability(capabilityKey);

        if (cap && cap.status === Statuses.UP) {
          return {
            value: agent.get('agent_id'),
            label: agent.get('display_name') || agent.get('host_name'),
            disabled: agent.get('status') !== Statuses.UP,
            ...agent.get()
          };
        }
        return null;
      })
      .filter(Boolean); // Filter out null values

    return orderBy(options, ['cdate'], ['desc']);
  }

  getKentikAgentWithKproxyConfig = (model) => {
    const capability = model.get('capabilities').find((cap) => cap.name === CAPABILITY_KEYS.KPROXY);
    if (capability) {
      const { config_id: configId } = capability;
      const { id: agentId } = model;
      if (configId && agentId) {
        return this.fetchCapabilityConfig(agentId, CAPABILITY_KEYS.KPROXY, configId).then((latestConfig) => ({
          flowProxyConfig: latestConfig || {},
          flowProxyId: agentId,
          flowProxyModel: model
        }));
      }
    }
    return Promise.resolve({});
  };

  @computed
  get kentikAgentOptions() {
    if (!this.collection.hasFetched) {
      this.collection.fetch();
    }

    const options = this.collection.map((agent) => ({
      value: agent.get('agent_id'),
      label: agent.get('display_name') || agent.get('host_name'),
      disabled: agent.get('status') !== Statuses.UP,
      ...agent.get()
    }));

    return orderBy(options, ['cdate'], ['desc']);
  }

  @computed
  get kentikAgentOptionsWithKproxy() {
    return this.kentikAgentOptionsWithCapability(CAPABILITY_KEYS.KPROXY);
  }

  @computed
  get kentikAgentOptionsWithKsqueegee() {
    return this.kentikAgentOptionsWithCapability(CAPABILITY_KEYS.KSQUEEGEE);
  }

  @computed
  get authorizedKentikAgentOptions() {
    return this.kentikAgentOptions.filter((m) => m.status !== Statuses.NOT_AUTHORIZED);
  }

  groupStatus = (group) => GROUP_HEALTH_LABELS[group.healthState] || 'Unknown';

  /**
   * Sort a list of capability keys alphabetically by their display title
   * (falling back to the raw key if no title is defined).
   * @param {string[]} [capabilityKeys]
   * @returns {string[]} a new, sorted array
   */
  sortCapabilityKeysByTitle = (capabilityKeys = []) =>
    [...capabilityKeys].sort((a, b) => {
      const titleA = CAPABILITY_TITLES[a] || a;
      const titleB = CAPABILITY_TITLES[b] || b;
      return titleA.toLowerCase().localeCompare(titleB.toLowerCase());
    });

  getNmsAssignmentOption(id) {
    return this.nmsAssignmentOptions.find((option) => option.value === id);
  }

  // Capability-agnostic lookup for a single cluster-group option by its `group:`-prefixed id (unlike getNmsAssignmentOption, which is Ranger-only); reuses getAssignmentOptionsForCapabilities with no capability filter (= match any capability).
  getClusterGroupOption(groupId) {
    return this.getAssignmentOptionsForCapabilities().find((option) => option.value === groupId);
  }

  /**
   * @param {boolean} [force=true]
   * @returns {Promise<boolean>}
   */
  getHasClusters(force = true) {
    return this.collection.getHasClustersUnfiltered(force);
  }

  getClusterById(id) {
    return this.clusters.fetch().then(() => this.clusters.findByClusterDataId(id));
  }

  getAssignmentOptionsForCapabilities(capabilityKeys) {
    const agentOpts = this.authorizedKentikAgentOptions.filter((m) => !m.cluster_id);
    const clusterOpts = [];
    // When multiple capabilities are requested (or none, i.e. "any capability" for getClusterGroupOption), disambiguate same-cluster options (e.g. Ranger + Kproxy) by appending the capability title to the label.
    const disambiguateLabel = !capabilityKeys || capabilityKeys.length > 1;
    for (const cluster of this.clusters.get()) {
      cluster.get('groups').forEach((groupWrapper) => {
        const { capability, groupId } = groupWrapper.group;
        if (capabilityKeys && !capabilityKeys.includes(capability)) {
          return;
        }
        clusterOpts.push({
          value: `${GROUP_ID_PREFIX}${groupId}`,
          label: disambiguateLabel
            ? `${cluster.name} (${CAPABILITY_LABEL_SUFFIX[capability] || capability})`
            : cluster.name,
          host_ip: `${groupWrapper.assignments.length} agents`,
          status: this.groupStatus(groupWrapper),
          ...cluster.get()
        });
      });
    }
    return [...agentOpts, ...clusterOpts];
  }

  @computed
  get nmsAssignmentOptions() {
    return this.getAssignmentOptionsForCapabilities([CAPABILITY_KEYS.RANGER]);
  }

  @computed
  get availableRunningKentikAgentOptions() {
    return this.kentikAgentOptions.filter((m) => m.status === Statuses.UP);
  }

  @action
  startPollingForCapabilityReadiness = (agentId, capabilityKey) =>
    new Promise((resolve, reject) => {
      const interval = setInterval(() => {
        this.collection
          .fetch({ force: true })
          .then(() => {
            const agent = this.collection.findByAgentId(agentId);
            const capabilities = agent.get('capabilities');
            const cap = capabilities.find((capability) => capability.name === capabilityKey);

            if (cap && cap.status === Statuses.UP) {
              resolve(agent);
              clearInterval(interval);
            } else {
              console.warn(`Waiting for ${capabilityKey} to be ready...`);
            }
          })
          .catch((err) => {
            console.error(`Error occurred during fetching agents: ${err}`);
            reject(err);
          });
      }, 1000);
    });

  // This updates the local data using some response data but does not alter anything on the server
  updateCapabilitiesWoFetch(agentId, capabilityKey, enable) {
    const agent = this.collection.findByAgentId(agentId);
    if (agent) {
      const capabilities = agent.get('capabilities');
      const cap = agent.getCapability(capabilityKey);
      if (cap) {
        if (enable) {
          // Concern: sometimes when you enable a capability, its actually Statuses.DOWN
          // but we won't know that until the unregistered call establishes that
          cap.status = Statuses.RUNNING;
        } else {
          cap.status = Statuses.DISABLED;
        }
      } else if (enable) {
        // need to add a temp node bc we just installed
        agent.set('capabilities', [...capabilities, { name: capabilityKey, status: Statuses.RUNNING }]);
      }
    }
  }

  getNextRefreshFromHeartbeat(agentId) {
    const agent = this.collection.findByAgentId(agentId);
    return agent ? parseInt(agent.get('last_seen')) + parseInt(agent.get('heartBeatPeriod')) : null;
  }

  fetchDevicesForAgent(agentId, capability = CAPABILITY_KEYS.RANGER) {
    return api
      .get(`/api/ui/kentik-agent/agents/devices/${encodeURIComponent(agentId)}`)
      .then((capabilityDeviceMap) => capabilityDeviceMap[capability]);
  }

  fetchAssociatedDevicesForAgent(agentId, capability, oldestTime) {
    const route = `/api/ui/kentik-agent/agents/associated-devices/${encodeURIComponent(agentId)}/${encodeURIComponent(
      capability
    )}`;
    const query = {};

    if (oldestTime) {
      query.oldest_time = oldestTime instanceof Date ? oldestTime.toISOString() : oldestTime;
    }

    return api.get(route, { query });
  }

  // //////// All V2 API calls:
  // Assumes an AUTHORIZED agent
  getKentikAgentDetails(agentId) {
    this.setLoading(true);
    return api.get(`/api/ui/kentik-agent/agents/${encodeURIComponent(agentId)}`).then((detailsResponse) => {
      this.setLoading(false);
      return detailsResponse;
    });
  }

  /**
   * Enable a capability on an agent
   * @param {string} agentId
   * @param {string} capabilityKey
   * @param {Object} [options] - API call options
   */
  enableCapability(agentId, capabilityKey, options = {}) {
    this.setLoading(true);
    return api
      .put(
        `/api/ui/kentik-agent/agents/capabilities/${encodeURIComponent(agentId)}/${encodeURIComponent(capabilityKey)}`,
        {
          body: {
            enabled: true,
            initial: false
          },
          ...options
        }
      )
      .then((res) => {
        this.setLoading(false);
        this.updateCapabilitiesWoFetch(agentId, capabilityKey, true);
        return res;
      });
  }

  /**
   * Disable a capability on an agent
   * @param {string} agentId
   * @param {string} capabilityKey
   * @param {Object} [options] - API call options
   */
  disableCapability(agentId, capabilityKey, options = {}) {
    this.setLoading(true);
    return api
      .put(
        `/api/ui/kentik-agent/agents/capabilities/${encodeURIComponent(agentId)}/${encodeURIComponent(capabilityKey)}`,
        {
          body: {
            enabled: false,
            initial: false
          },
          ...options
        }
      )
      .then((res) => {
        this.setLoading(false);
        this.updateCapabilitiesWoFetch(agentId, capabilityKey, false);
        return res;
      });
  }

  /**
   * Ensure a capability is enabled on an agent, using updateCapability if already installed
   * or installCapability for a first-time install. Avoids unnecessary re-init side effects.
   * @param {string} agentId
   * @param {string} capabilityKey
   */
  ensureCapabilityEnabled(agentId, capabilityKey) {
    const agent = this.collection?.findByAgentId(agentId);
    const existingCap = agent?.getCapability(capabilityKey);
    return existingCap
      ? this.updateCapability(agentId, capabilityKey, true)
      : this.installCapability(agentId, capabilityKey);
  }

  /**
   * Enable or disable a capability on an agent
   * @param {string} agentId
   * @param {string} capabilityKey
   * @param {boolean} enable - true to enable, false to disable
   * @param {Object} [options] - API call options
   */
  updateCapability(agentId, capabilityKey, enable = true, options = {}) {
    return enable
      ? this.enableCapability(agentId, capabilityKey, options)
      : this.disableCapability(agentId, capabilityKey, options);
  }

  /**
   * Atomically enable and/or disable a set of capabilities on an agent via the
   * SwitchAgentCapabilities Temporal workflow, which enables every `enableCapabilityKeys` entry
   * first, then uninstalls every `disableCapabilityKeys` entry - rolling back every change it made
   * if any single step fails.
   *
   * Rejects with an Error carrying `requiresManualIntervention: true` if a rollback itself failed,
   * meaning the agent may be left in a mixed state that needs manual reconciliation.
   *
   * @param {Object} params
   * @param {string} params.agentId
   * @param {string[]} [params.enableCapabilityKeys]
   * @param {string[]} [params.disableCapabilityKeys]
   * @param {Object} [params.enableCapabilityConfigs] - config (argsObject/values) to write, keyed by
   *   capability key, for entries in enableCapabilityKeys
   * @returns {Promise<void>}
   */
  switchAgentCapabilities({ agentId, enableCapabilityKeys = [], disableCapabilityKeys = [], enableCapabilityConfigs }) {
    return api
      .post(`/api/ui/kentik-agent/agents/switch-capabilities/${encodeURIComponent(agentId)}`, {
        body: { enableCapabilityKeys, disableCapabilityKeys, enableCapabilityConfigs }
      })
      .then((result) => {
        if (result.success) {
          return result;
        }
        const error = new Error(result.message);
        error.requiresManualIntervention = result.requiresManualIntervention;
        throw error;
      });
  }

  /**
   * Switch the Flow Proxy delivery mode.
   *
   * If the previous engine isn't actually installed, this is a plain install rather than a mode
   * switch, so it goes straight through the Kentik Agent enable/install API - no workflow needed.
   * Only switching modes on an already-installed FlowProxy capability gets routed through the
   * atomic switchAgentCapabilities workflow.
   *
   * @param {Object} params
   * @param {string} params.agentId
   * @param {string} params.targetKey - capability key to enable (e.g. ktraffic)
   * @param {string} params.sourceKey - capability key to uninstall (e.g. kproxy)
   * @param {Object} [params.configUpdateParams] - config (argsObject/values) to bundle atomically with
   *   enabling targetKey; only applied when actually routed through the atomic switch workflow
   * @returns {Promise<void>}
   */
  switchFlowProxyMode({ agentId, targetKey, sourceKey, configUpdateParams }) {
    if (sourceKey === targetKey) {
      return Promise.resolve();
    }

    const agent = this.collection?.findByAgentId(agentId);
    const sourceInstalled = !!agent?.getCapability(sourceKey);

    if (!sourceInstalled) {
      // No previous engine installed on this agent - this is a plain install, not a mode switch.
      return this.ensureCapabilityEnabled(agentId, targetKey);
    }

    return this.switchAgentCapabilities({
      agentId,
      enableCapabilityKeys: [targetKey],
      disableCapabilityKeys: [sourceKey],
      enableCapabilityConfigs: configUpdateParams ? { [targetKey]: configUpdateParams } : undefined
    });
  }

  authorizeAgent(installId) {
    this.setLoading(true);
    return api.post(`/api/ui/kentik-agent/agents/authorize/${encodeURIComponent(installId)}`).then((responseObject) => {
      this.setLoading(false);
      if (responseObject.success === false) {
        throw new Error('Error registering Agent');
      }
      return responseObject.agent_id;
    });
  }

  @observable
  installCommands = null;

  @observable
  installCommandsError = null;

  @observable
  isRetryingInstallCommands = false;

  // bumped by clearInstallCommands so a response that lands after unmount can't restore the token
  installCommandsRequestId = 0;

  @action
  fetchInstallCommands() {
    if (this.installCommands) {
      return Promise.resolve(this.installCommands);
    }

    this.installCommandsRequestId += 1;
    const requestId = this.installCommandsRequestId;

    return api
      .post('/api/ui/kentik-agent/agents/install-commands', { body: {}, showErrorToast: false })
      .then(
        action((response) => {
          if (requestId !== this.installCommandsRequestId) {
            return undefined;
          }

          this.installCommands = response;
          this.installCommandsError = null;
          this.isRetryingInstallCommands = false;
          return response;
        })
      )
      .catch(
        action((err) => {
          if (requestId !== this.installCommandsRequestId) {
            return;
          }

          this.installCommandsError = err || new Error('Failed to load install commands');
          this.isRetryingInstallCommands = false;
        })
      );
  }

  @action
  clearInstallCommands() {
    this.installCommandsRequestId += 1;
    this.installCommands = null;
    this.installCommandsError = null;
    this.isRetryingInstallCommands = false;
  }

  @action
  retryInstallCommands() {
    this.clearInstallCommands();
    this.isRetryingInstallCommands = true;
    return this.fetchInstallCommands();
  }

  /**
   * Install a capability for the first time and enable it
   * @param {string} agentId
   * @param {string} capabilityKey
   * @param {Object} [options] - API call options (e.g. rawResponse, suppressNotFoundErrors)
   */
  installCapability(agentId, capabilityKey, options = {}) {
    this.setLoading(true);
    return api
      .put(
        `/api/ui/kentik-agent/agents/installcapability/${encodeURIComponent(agentId)}/${encodeURIComponent(
          capabilityKey
        )}`,
        {
          body: {
            enabled: true,
            initial: true
          },
          ...options
        }
      )
      .then((responseObject) => {
        this.setLoading(false);
        this.updateCapabilitiesWoFetch(agentId, capabilityKey, true);
        return responseObject;
      });
  }

  // Assumes an AUTHORIZED agent
  getAgentCapabilities(agentId) {
    this.setLoading(true);
    return api
      .get(`/api/ui/kentik-agent/agents/capabilities/${encodeURIComponent(agentId)}`)
      .then((capabilitiesDetailsResponse) => {
        this.setLoading(false);
        return capabilitiesDetailsResponse;
      });
  }

  /**
   * Fetch configuration for a capability on an agent
   * @param {string} agentId
   * @param {string} capabilityKey
   * @param {string} configName
   */
  fetchCapabilityConfig(agentId, capabilityKey, configName) {
    this.setLoading(true);
    const queryParam = configName ? `?configName=${encodeURIComponent(configName)}` : '';
    return api
      .get(
        `/api/ui/kentik-agent/agents/capabilitiesconfig/${encodeURIComponent(agentId)}/${encodeURIComponent(
          capabilityKey
        )}${queryParam}`
      )
      .then((capabilitiesConfigResponse) => {
        this.setLoading(false);
        return capabilitiesConfigResponse;
      });
  }

  /**
   * Fetch a capability's metadata and its latest config in one call.
   * Normalizes `argsObject` to an object so callers can rely on its presence.
   * @param {string} agentId
   * @param {string} capabilityKey
   * @param {string} [configId]
   * @returns {Promise<{ metadata: Object|null, config: Object|null }>}
   */
  loadCapabilityConfig(agentId, capabilityKey, configId) {
    if (!capabilityKey) {
      return Promise.resolve({ metadata: null, config: null });
    }

    return this.fetchCapabilityMetadata(capabilityKey)
      .catch((err) => {
        console.warn(`Failed to fetch metadata for ${capabilityKey}`, err);
        return null;
      })
      .then((metadata) => {
        if (!agentId) {
          return { metadata, config: null };
        }

        return this.fetchCapabilityConfig(agentId, capabilityKey, configId)
          .then((config) => {
            const normalized = config || {};
            if (!normalized.argsObject) {
              normalized.argsObject = {};
            }
            return { metadata, config: normalized };
          })
          .catch((error) => {
            console.warn(`Failed to fetch capability config for ${capabilityKey}:`, error);
            return { metadata, config: null };
          });
      });
  }

  /**
   * Fetch all capability metadata at once and cache them
   * @returns {Promise<Object>} - Map of capabilityKey -> metadata
   */
  fetchAllCapabilityMetadata() {
    // Return existing promise if already fetching
    if (this.metadataFetchPromise) {
      return this.metadataFetchPromise;
    }

    // Return cached if all metadata already loaded
    if (this.metadataHasFetched) {
      return Promise.resolve(this.capabilityMetadataCache);
    }

    this.setLoading(true);
    this.metadataFetchPromise = api
      .get('/api/ui/kentik-agent/capabilities/metadata')
      .then((response) => response || {})
      .catch((error) => {
        if (error.status === 404 || error.statusCode === 404) {
          return {};
        }
        throw error;
      })
      .then((metadata) => {
        runInAction(() => {
          this.capabilityMetadataCache = metadata;
          this.metadataHasFetched = true;
        });
        return metadata;
      })
      .finally(() => {
        this.metadataFetchPromise = null;
        this.setLoading(false);
      });

    return this.metadataFetchPromise;
  }

  /**
   * Fetch metadata for a specific capability's configurable parameters
   * Fetches only the individual capability metadata, not all metadata
   * @param {string} capabilityKey
   * @returns {Promise<Object|null>} The metadata for the capability, or null if not found
   */
  fetchCapabilityMetadata(capabilityKey) {
    // Return from cache if available (including cached null for 404s)
    if (capabilityKey in this.capabilityMetadataCache) {
      return Promise.resolve(this.capabilityMetadataCache[capabilityKey]);
    }

    this.setLoading(true);
    return api
      .get(`/api/ui/kentik-agent/capabilities/${encodeURIComponent(capabilityKey)}/metadata`)
      .then((metadataResponse) => {
        this.setLoading(false);
        // Cache the result
        this.capabilityMetadataCache[capabilityKey] = metadataResponse;
        return metadataResponse;
      })
      .catch((error) => {
        this.setLoading(false);

        if (error.status === 404 || error.statusCode === 404) {
          this.capabilityMetadataCache[capabilityKey] = null;
          return null;
        }

        throw error;
      });
  }

  /**
   * Get cached metadata synchronously (returns null if not cached)
   * @param {string} capabilityKey
   * @returns {Object|null}
   */
  getCapabilityMetadata(capabilityKey) {
    if (!(capabilityKey in this.capabilityMetadataCache)) {
      return null;
    }

    return this.capabilityMetadataCache[capabilityKey];
  }

  /**
   * Whether a capability declares configurable parameters, based on cached metadata.
   * @param {string} capabilityKey
   * @returns {boolean}
   */
  supportsConfig(capabilityKey) {
    return capabilitySupportsConfig(this.getCapabilityMetadata(capabilityKey));
  }

  isFieldVisible = (fieldConfig) => {
    const { $auth } = this.store;

    if (!fieldConfig) {
      return false;
    }
    if (fieldConfig.blockInUI) {
      return false;
    }
    if (fieldConfig.userControlled !== true) {
      return false;
    }
    if (fieldConfig.sudoRequired && !$auth.isSudoer) {
      return false;
    }
    return !(fieldConfig.featureFlag && !$auth.hasPermission(fieldConfig.featureFlag, { overrideForSudo: false }));
  };

  /**
   * Filter callback for individual dropdown options within a config parameter.
   * Removes options gated by a featureFlag the current user lacks.
   * @param {{ featureFlag?: string }} option
   * @returns {boolean}
   */
  isOptionPermitted = (option) => {
    const { $auth } = this.store;

    if (!option?.featureFlag) {
      return true;
    }
    return $auth.hasPermission(option.featureFlag, { overrideForSudo: false });
  };

  buildVisibleConfigOptions = (metadata) => {
    const options = [];
    Object.entries(metadata).forEach(([argKey, paramObject]) => {
      if (paramObject.dependsOn || !this.isFieldVisible(paramObject)) {
        return;
      }
      const entry = {
        label: paramObject.label || argKey,
        value: argKey,
        sudoRequired: paramObject.sudoRequired || false
      };

      if (Array.isArray(paramObject.options)) {
        entry.filteredOptions = paramObject.options.filter((opt) => this.isOptionPermitted(opt));
      }

      options.push(entry);
    });
    return options;
  };

  /**
   * Get the transformed + version-filtered config metadata for a capability.
   * Performs: raw API metadata → transformConfigMetadataToOldFormat → filterConfigMetadataByVersion.
   * Results are cached by capabilityKey + capabilityVersion.
   *
   * @param {string} capabilityKey
   * @param {string} [capabilityVersion]
   * @returns {Object|null} The transformed LegacyConfigMetadata record, or null if unavailable
   */
  getTransformedConfigMetadata = (capabilityKey, capabilityVersion) => {
    const cacheKey = `${capabilityKey}@${capabilityVersion || ''}`;

    if (this.transformedConfigCache[cacheKey]) {
      return this.transformedConfigCache[cacheKey];
    }

    const rawMetadata = this.capabilityMetadataCache[capabilityKey];

    if (!rawMetadata) {
      return null;
    }

    let transformed;

    // Empty object from API means no schema — fall back to constants
    if (typeof rawMetadata === 'object' && Object.keys(rawMetadata).length === 0) {
      transformed = getRawCapabilityConfigs(capabilityKey);
    } else {
      transformed = transformConfigMetadataToOldFormat(rawMetadata);
    }

    const result = filterConfigMetadataByVersion(transformed, capabilityVersion);
    this.transformedConfigCache[cacheKey] = result;
    return result;
  };

  /**
   * Returns the list of user-controllable config options for a capability,
   * filtered by blockInUI, dependsOn, sudo, and feature flag permissions for the current user.
   * Each returned entry may include a `filteredOptions` array when the parameter has
   * dropdown options gated by featureFlag.
   * When argsObject is provided, also includes args that exist in metadata and
   * pass visibility checks but aren't userControlled (e.g. already configured on the server).
   *
   * @param {string} capabilityKey
   * @param {string} [capabilityVersion]
   * @param {Object} [argsObject] - Current args (from server config or form state)
   * @returns {Array<{label: string, value: string, sudoRequired?: boolean, filteredOptions?: Array}>}
   */
  getVisibleConfigOptions = (capabilityKey, capabilityVersion, argsObject) => {
    const metadata = this.getTransformedConfigMetadata(capabilityKey, capabilityVersion);

    if (!metadata) {
      return [];
    }

    const options = this.buildVisibleConfigOptions(metadata);

    if (!argsObject) {
      return options;
    }

    Object.keys(argsObject).forEach((argKey) => {
      if (options.some((option) => option.value === argKey)) {
        return;
      }

      const fieldConfig = metadata[argKey];

      if (!this.isFieldVisible(fieldConfig)) {
        return;
      }

      options.push({
        label: fieldConfig.label || argKey,
        value: argKey
      });
    });

    return options;
  };

  findNameByGroupId(groupId) {
    let realId = groupId;
    if (typeof groupId === 'string' && groupId.startsWith(GROUP_ID_PREFIX)) {
      realId = groupId.slice(GROUP_ID_PREFIX.length);
    }
    const cluster = this.clusters._clusterByGroupID[realId];
    if (cluster) {
      return cluster.displayName || cluster.clusterId;
    }
    return groupId;
  }

  /**
   * Save configuration changes for a capability
   * @param {string} agentId
   * @param {string} capabilityKey
   * @param {object} updateParams
   * @param {string} configName
   */
  saveCapabilityConfig(agentId, capabilityKey, updateParams, configName) {
    this.setLoading(true);
    const queryParam = configName ? `?configName=${encodeURIComponent(configName)}` : '';
    return api
      .put(
        `/api/ui/kentik-agent/agents/capabilitiesconfig/${encodeURIComponent(agentId)}/${encodeURIComponent(
          capabilityKey
        )}${queryParam}`,
        {
          body: { updateParams }
        }
      )
      .then((capabilitiesConfigResponse) => {
        this.setLoading(false);
        return capabilitiesConfigResponse;
      });
  }

  updateMetadata(agentId, options) {
    this.setLoading(true);
    let reloadSites = false;

    // name, description, site
    const { display_name, description, site, closest_device_id } = options;

    if (site.lat) {
      reloadSites = true;
    }

    return api
      .put(`/api/ui/kentik-agent/agents/${encodeURIComponent(agentId)}`, {
        body: { name: display_name, description, site, closest_device_id }
      })
      .then((res) => {
        this.setLoading(false);
        if (reloadSites) {
          return this.store.$sites.collection.fetch({ force: true }).then(() => res);
        }
        return res;
      });
  }

  /**
   * Restart a capability process
   * @param {string} agentId
   * @param {string} capabilityKey
   * @param {Object} [options] - API call options
   */
  restartCapability(agentId, capabilityKey, options = {}) {
    this.setLoading(true);
    return api
      .post(
        `/api/ui/kentik-agent/agents/restartcapability/${encodeURIComponent(agentId)}/${encodeURIComponent(
          capabilityKey
        )}`,
        { ...options }
      )
      .then(() => {
        this.setLoading(false);
      });
  }

  /**
   * Uninstall a capability from an agent
   * @param {string} agentId
   * @param {string} capabilityKey
   * @param {Object} [options] - API call options
   */
  uninstallCapability(agentId, capabilityKey, options = {}) {
    this.setLoading(true);
    return api
      .post(
        `/api/ui/kentik-agent/agents/uninstallcapability/${encodeURIComponent(agentId)}/${encodeURIComponent(
          capabilityKey
        )}`,
        { ...options }
      )
      .then(() => {
        this.setLoading(false);
      });
  }

  lookupAgents(agentIds = []) {
    const lookupIds = [];
    const cachedResults = agentIds.reduce((acc, agentId) => {
      const cachedResult = this.agentLookups[agentId];
      if (cachedResult) {
        acc.push(cachedResult);
      } else {
        lookupIds.push(agentId);
      }
      return acc;
    }, []);

    if (!lookupIds.length) {
      return Promise.resolve().then(() => cachedResults);
    }
    return api.post('/api/ui/kentik-agent/agents/lookup', { body: { agent_ids: lookupIds } }).then((agents) => {
      agents.forEach((agent) => {
        this.agentLookups[agent.agent_id] = agent;
      });
      return [...cachedResults, ...agents];
    });
  }
}

export default new KentikAgentStore();
